import React, { useContext, useState, useCallback, useEffect } from 'react';
import throttle from 'lodash.throttle';

const BREAKPOINTS = {
  small: {
    min: 0,
    max: 640
  },
  medium: {
    min: 641,
    max: 960
  },
  large: {
    min: 961,
    max: Infinity
  }
};
const ViewportContext = React.createContext(null);

const useViewport = () => {
  const viewport = useContext(ViewportContext);

  if (!viewport) {
    throw new Error("You must use a ViewportProvider to get a viewport.");
  }

  return viewport;
};

const getViewportMatch = target => {
  const {
    innerWidth
  } = target;
  let breakpoint;

  for (breakpoint in BREAKPOINTS) {
    const {
      min,
      max
    } = BREAKPOINTS[breakpoint];
    if (innerWidth >= min && innerWidth <= max) return breakpoint;
  }

  return null;
};

const Provider = ({
  children,
  target = window
}) => {
  const [viewport, setViewport] = useState(getViewportMatch(target));
  const setNewSize = useCallback(() => {
    setViewport(getViewportMatch(target));
  }, [target]);
  const resizeCallback = useCallback(throttle(setNewSize, 200), []);
  const handleResize = useCallback(() => {
    resizeCallback();
  }, [resizeCallback]);
  useEffect(() => {
    setNewSize();
    if (!target || !target.addEventListener) return;
    target.addEventListener("resize", handleResize, true);
    return () => {
      resizeCallback.cancel();
      if (!target || !target.removeEventListener) return;
      target.removeEventListener("resize", handleResize, true);
    };
  }, [target, resizeCallback, setNewSize, handleResize]);
  return React.createElement(ViewportContext.Provider, {
    value: viewport
  }, children);
};

const ViewportProvider = ({
  children
}) => React.createElement(Provider, null, children);

export default ViewportProvider;
export { ViewportContext, useViewport };
